// ─────────────────────────────────────────────────────────────────────────
// Blog content source of truth.
// Each post is keyed by slug and rendered by blog-details/[slug]/page.jsx.
// Block types supported by the renderer:
//   { type: "paragraph", text }
//   { type: "heading", text }
//   { type: "tldr", items: [string] }
//   { type: "callout", title, text }
//   { type: "table", headers: [string], rows: [[string]] }
//   { type: "steps", items: [{ step, title, desc }] }
//   { type: "checkGrid", items: [{ label, applies, note }] }
//   { type: "errorCards", items: [{ code, title, fix }] }
//   { type: "list", items: [string] }
// ─────────────────────────────────────────────────────────────────────────

export const BLOG_POSTS = {
  // 1 ──────────────────────────────────────────────────────────────────────
  "e-invoice-irn-automatic-b2b": {
    slug: "e-invoice-irn-automatic-b2b",
    image: "/images/blog-e-invoice.jpg",
    title:
      "How to Generate E-Invoice (IRN) Automatically for Your B2B Transactions",
    excerpt:
      "E-Invoice is mandatory for eligible taxpayers. Here's exactly how ABSS Books generates the Invoice Reference Number (IRN) automatically - and what you need to set up first.",
    category: "GST & Compliance",
    tag: "E-Invoice",
    date: "12 May 2026",
    readTime: "6 min read",
    author: { name: "Priya Sharma", role: "GST & Compliance Writer, ABSS", initial: "P" },
    heroBadge: "Mandatory for turnover above ₹5 crore from Aug 2023",
    intro: [
      "If your business has a turnover above ₹5 crore, E-Invoice is not optional - it's mandatory for every B2B transaction. The problem is that most accounting software treats it as an afterthought: a separate portal login, manual JSON uploads, or an expensive CA who does it at month-end.",
      "ABSS Books generates the Invoice Reference Number (IRN) automatically when you create a qualifying invoice - without leaving the app, without extra steps, without a separate login. This guide explains exactly how it works and what you need to set up.",
    ],
    tldr: [
      "E-Invoice (IRN) is mandatory for B2B invoices if your annual turnover > ₹5 crore",
      "ABSS Books auto-generates IRN when you save a qualifying invoice",
      "The QR code is embedded in your PDF automatically",
      "Setup requires linking your GSTIN and IRP credentials - one-time config",
    ],
    sections: [
      { type: "heading", text: "What is E-Invoice and Who Needs It?" },
      {
        type: "paragraph",
        text: "E-Invoice (electronic invoicing) under the GST framework means that certain B2B invoices must be registered on the Invoice Registration Portal (IRP) before they can be issued. The IRP validates the invoice and returns a unique Invoice Reference Number (IRN), a digitally signed QR code, and a signed invoice JSON.",
      },
      {
        type: "paragraph",
        text: "The IRN must be present on your invoice PDF. Without it, the invoice is invalid for your buyer's input tax credit (ITC) claim - which means they'll reject the invoice or refuse payment until you fix it.",
      },
      {
        type: "table",
        headers: ["Annual Turnover", "E-Invoice Mandatory From"],
        rows: [
          ["Above ₹500 crore", "October 2020"],
          ["₹100–500 crore", "January 2021"],
          ["₹50–100 crore", "April 2021"],
          ["₹20–50 crore", "April 2022"],
          ["₹10–20 crore", "October 2022"],
          ["₹5–10 crore", "August 2023"],
          ["Below ₹5 crore", "Not yet mandatory"],
        ],
      },
      { type: "heading", text: "One-Time Setup in ABSS Books" },
      {
        type: "paragraph",
        text: "Before ABSS Books can auto-generate IRNs, you need to link your GSTIN to the Invoice Registration Portal (IRP). This is a one-time configuration.",
      },
      {
        type: "steps",
        items: [
          { step: "01", title: "Go to Settings → GST Configuration", desc: "In ABSS Books, navigate to Settings and open the GST Configuration panel. Enter your GSTIN and verify it - ABSS auto-fills your legal name and address from the GST portal." },
          { step: "02", title: "Enable E-Invoice and enter IRP credentials", desc: "Toggle on 'E-Invoice Integration'. Enter your IRP username and password (the credentials you created on einvoice1.gst.gov.in). ABSS stores these encrypted - never in plain text." },
          { step: "03", title: "Test with a draft invoice", desc: "Create a test B2B invoice and save it. ABSS will attempt IRN generation automatically. If successful, you'll see the IRN and QR code appear on the invoice. If there's an error, ABSS shows the exact IRP error code." },
          { step: "04", title: "You're live", desc: "From this point, every qualifying B2B invoice saved in ABSS Books automatically goes to the IRP, gets an IRN, and has the QR code embedded in the PDF. No manual steps required." },
        ],
      },
      { type: "heading", text: "Which Invoices Get an IRN Automatically?" },
      {
        type: "paragraph",
        text: "Not every invoice requires E-Invoice. ABSS Books checks the following conditions automatically before attempting IRN generation:",
      },
      {
        type: "checkGrid",
        items: [
          { label: "B2B invoices", applies: true, note: "Buyer has a GSTIN - always triggers IRN" },
          { label: "B2C invoices (large)", applies: true, note: "Above ₹2.5 lakh for certain categories" },
          { label: "Export invoices", applies: true, note: "LUT/bond exports also need IRN" },
          { label: "B2C invoices (small)", applies: false, note: "Below ₹2.5 lakh - IRN not required" },
          { label: "Credit notes & debit notes", applies: true, note: "Against eligible original invoices" },
          { label: "Delivery challans", applies: false, note: "Not a tax invoice - no IRN required" },
        ],
      },
      { type: "heading", text: "Common Errors and How to Fix Them" },
      {
        type: "paragraph",
        text: "ABSS Books surfaces the exact IRP error code when IRN generation fails. Here are the most common ones and what to do:",
      },
      {
        type: "errorCards",
        items: [
          { code: "2150", title: "Duplicate IRN", fix: "This invoice already has an IRN generated - check if you're creating a duplicate. The original IRN will appear in the invoice record." },
          { code: "2283", title: "Buyer GSTIN not found", fix: "The GSTIN entered for your buyer is incorrect or inactive. Go to the Customer Master, update their GSTIN, and re-validate against the GST portal." },
          { code: "2176", title: "HSN code missing or invalid", fix: "Every line item must have a valid HSN/SAC code. Open your Item Master, add the correct HSN code to the item, and re-save the invoice." },
          { code: "IRP timeout", title: "IRP server unavailable", fix: "The IRP portal sometimes has downtime. ABSS Books queues the IRN request and retries automatically - you'll get a notification when it succeeds." },
        ],
      },
    ],
    cta: {
      badge: "Get Started",
      title: "Set Up E-Invoice in ABSS Books Today",
      description: "One-time configuration. Auto-IRN generation on every qualifying invoice after that.",
      primary: { label: "Book a Demo", href: "/contact" },
      secondary: { label: "Read the Docs", href: "/documentation" },
    },
    related: ["e-way-bill-for-manufacturers", "desktop-vs-cloud-accounting-2026"],
  },

  // 2 ──────────────────────────────────────────────────────────────────────
  "desktop-vs-cloud-accounting-2026": {
    slug: "desktop-vs-cloud-accounting-2026",
    image: "/images/blog-desktop-vs-cloud.jpg",
    title: "Desktop vs Cloud Accounting: A Practical Comparison for Indian SMEs in 2026",
    excerpt:
      "Desktop accounting software has served Indian businesses for decades. But cloud accounting offers things a desktop install simply can't - here's an honest side-by-side.",
    category: "Accounting & Finance",
    tag: "Comparison",
    date: "8 May 2026",
    readTime: "7 min read",
    author: { name: "Rahul Mehta", role: "Accounting Product Lead, ABSS", initial: "R" },
    heroBadge: "An honest, feature-by-feature look at both",
    intro: [
      "Desktop accounting software has earned its place. For a generation of Indian businesses it was the first real accounting system, and it still does the core job well. But the way businesses work has changed - teams are distributed, owners want numbers on their phone, and GST compliance happens with every invoice, not once a quarter.",
      "This is not an argument that desktop software is bad. It's an honest comparison to help you decide what fits your business in 2026: a proven desktop install, or a cloud platform like ABSS Books that also carries a built-in CRM.",
    ],
    tldr: [
      "Desktop software runs on one PC; cloud accounting runs in any browser and on mobile",
      "Cloud platforms like ABSS can bundle a CRM - most desktop packages have no sales pipeline",
      "Both can handle Indian GST; ABSS generates E-Invoice and E-Way Bill inside the product",
      "Moving to the cloud uses the Ledger and Item exports you already have",
    ],
    sections: [
      { type: "heading", text: "The Core Difference: Where Your Books Live" },
      {
        type: "paragraph",
        text: "Desktop accounting software is installed on a Windows PC. To use it remotely or across a team you usually buy a multi-user licence and add either a local server or a remote-desktop hosting subscription. ABSS Books is cloud-native - you log in from Chrome, Safari, or the mobile app on any device, and everyone sees the same live data.",
      },
      {
        type: "paragraph",
        text: "For a single-location business with one operator, that difference may not matter much. For a growing team - a sales rep in the field, an accountant at the office, an owner travelling - it changes daily work entirely.",
      },
      { type: "heading", text: "Feature-by-Feature" },
      {
        type: "table",
        headers: ["Capability", "Cloud (ABSS Books)", "Typical Desktop Software"],
        rows: [
          ["Access", "Any browser, Mac, Windows, mobile", "Windows PC (remote desktop for access elsewhere)"],
          ["Multi-user", "Included with role permissions", "Extra licence + server setup"],
          ["Built-in CRM", "Yes - leads, quotations, visits", "Usually no"],
          ["E-Invoice & E-Way Bill", "Generated inside ABSS", "Often needs add-ons or the GST portal"],
          ["Backups", "Automatic cloud backup", "Manual local backup"],
          ["Updates", "Automatic, no reinstall", "Download and install each release"],
        ],
      },
      { type: "heading", text: "Where Desktop Software Still Wins" },
      {
        type: "paragraph",
        text: "Desktop packages work fully offline, many accountants already know them well, and years of customisation can be hard to walk away from. If your business is stable, single-location, and your accountant is happy, there is no urgent reason to switch.",
      },
      {
        type: "callout",
        title: "When to switch",
        text: "Consider cloud accounting when you add a second location, when your sales team needs a pipeline, when you want owners and CAs to see live numbers remotely, or when manual E-Invoice / E-Way Bill steps are slowing down dispatch.",
      },
      { type: "heading", text: "Moving to the Cloud Without Losing History" },
      {
        type: "steps",
        items: [
          { step: "01", title: "Export from your current software", desc: "Export Ledgers, Groups, Item Masters, and Opening Balances to XML or Excel." },
          { step: "02", title: "Import into ABSS", desc: "Bulk import brings in your customers, items, opening stock, and opening balances." },
          { step: "03", title: "Verify and go live", desc: "The ABSS team helps you reconcile opening balances so your books match to the rupee before you start." },
        ],
      },
    ],
    cta: {
      badge: "See It Yourself",
      title: "See How ABSS Books Fits Your Workflow",
      description: "Book a 30-minute walkthrough and we'll map your exact workflow - migration included.",
      primary: { label: "Book a Demo", href: "/contact" },
      secondary: { label: "Explore ABSS Books", href: "/features/books" },
    },
    related: ["e-invoice-irn-automatic-b2b", "multi-branch-accounting", "lead-to-invoice-crm-books-integration"],
  },


  // 4 ──────────────────────────────────────────────────────────────────────
  "qr-code-lead-capture-trade-shows": {
    slug: "qr-code-lead-capture-trade-shows",
    image: "/images/blog-qr-lead-capture.jpg",
    title: "QR Code Lead Capture at Trade Shows: How to Never Lose a Lead Again",
    excerpt:
      "Your sales team meets 200 people at an expo. 150 of those leads get lost in notes, WhatsApp messages, and sticky papers. Here's how ABSS CRM's QR capture fixes this permanently.",
    category: "CRM & Sales",
    tag: "Lead Management",
    date: "28 Apr 2026",
    readTime: "5 min read",
    author: { name: "Ananya Iyer", role: "CRM & Sales Writer, ABSS", initial: "A" },
    heroBadge: "Turn a business card into a CRM lead in seconds",
    intro: [
      "Trade shows are expensive - the stall, the travel, the team's time. Yet the most common way leads are captured is still a stack of business cards and a few scribbled notes that nobody enters into a system until the following week, by which point half the context is gone.",
      "ABSS CRM replaces that with a QR code. A visitor scans it, fills a short form on their own phone, and the lead lands in your pipeline instantly - tagged to the event, the rep, and the booth.",
    ],
    tldr: [
      "A single QR code turns any visitor's phone into a lead-capture form",
      "Leads land in the CRM instantly, tagged to the event and the rep",
      "No manual card entry, no leads lost after the show",
      "Follow-up sequences can trigger automatically the same day",
    ],
    sections: [
      { type: "heading", text: "Why Card-and-Notebook Capture Fails" },
      {
        type: "paragraph",
        text: "The problem isn't your team - it's the workflow. Cards pile up, notes lose their context, and data entry is a chore that slips for days. By the time leads reach a spreadsheet, the hot ones have gone cold and nobody remembers who said what.",
      },
      {
        type: "checkGrid",
        items: [
          { label: "Leads entered same day", applies: true, note: "Captured at the booth, not next week" },
          { label: "Context preserved", applies: true, note: "Event, booth, rep, and notes attached" },
          { label: "Duplicate cards re-typed", applies: false, note: "No manual entry to duplicate" },
          { label: "Leads lost after the show", applies: false, note: "Everything is already in the CRM" },
        ],
      },
      { type: "heading", text: "How QR Capture Works in ABSS CRM" },
      {
        type: "steps",
        items: [
          { step: "01", title: "Create a capture form", desc: "In ABSS CRM, build a short lead form - name, company, phone, requirement - and tag it to the event (e.g. 'Auto Expo 2026')." },
          { step: "02", title: "Print or display the QR", desc: "ABSS generates a QR code for that form. Put it on the booth banner, a standee, or the rep's phone." },
          { step: "03", title: "Visitor self-fills", desc: "The visitor scans and submits on their own phone in 20 seconds. The lead appears in your pipeline instantly, tagged to the event and rep." },
          { step: "04", title: "Auto follow-up", desc: "Trigger a welcome message and assign the lead to the right salesperson automatically - before the visitor has left your stall." },
        ],
      },
      { type: "heading", text: "After the Show: Measure What the Booth Was Worth" },
      {
        type: "paragraph",
        text: "Because every lead is tagged to the event, you can measure the show's real return: leads captured, meetings booked, quotes raised, and deals won - all filtered by that single event tag. Next year's stall budget becomes a decision backed by numbers, not a gut feel.",
      },
      {
        type: "callout",
        title: "From lead to invoice",
        text: "A won lead in ABSS CRM converts into a quote and then a GST invoice in ABSS Books without re-entering anything - the same contact and items carry straight through.",
      },
    ],
    cta: {
      badge: "Capture More",
      title: "Never Lose Another Trade-Show Lead",
      description: "Set up a QR capture form for your next event and watch leads land in the pipeline live.",
      primary: { label: "Book a Demo", href: "/contact" },
      secondary: { label: "Explore ABSS CRM", href: "/features/crm" },
    },
    related: ["signs-outgrown-excel-need-crm", "lead-to-invoice-crm-books-integration", "desktop-vs-cloud-accounting-2026"],
  },

  // 5 ──────────────────────────────────────────────────────────────────────
  "signs-outgrown-excel-need-crm": {
    slug: "signs-outgrown-excel-need-crm",
    image: "/images/blog-excel-to-crm.jpg",
    title: "5 Signs Your Business Has Outgrown Excel and Needs a Real CRM",
    excerpt:
      "Most Indian SMEs start with spreadsheets. Here are the five inflection points where Excel stops working - and what you should move to instead.",
    category: "CRM & Sales",
    tag: "Growth",
    date: "22 Apr 2026",
    readTime: "4 min read",
    author: { name: "Ananya Iyer", role: "CRM & Sales Writer, ABSS", initial: "A" },
    heroBadge: "Know when the spreadsheet is costing you deals",
    intro: [
      "Excel is where almost every business starts tracking customers, and for a while it's perfect - flexible, familiar, free. But a spreadsheet has no memory of who followed up, no reminders, and no single version of the truth once more than one person touches it.",
      "Here are five clear signs your sales tracking has outgrown Excel, and what a real CRM changes at each point.",
    ],
    tldr: [
      "Leads slip because nothing reminds anyone to follow up",
      "Two people edit two copies and the numbers stop agreeing",
      "You can't answer 'what's in the pipeline?' without an afternoon of work",
      "A CRM gives one shared, reminder-driven, reportable system",
    ],
    sections: [
      { type: "heading", text: "Sign 1: Follow-ups Depend on Memory" },
      {
        type: "paragraph",
        text: "If whether a lead gets called back depends on someone remembering, you're losing deals you already paid to generate. A CRM attaches a next-action and a due date to every lead, so nothing goes quiet by accident.",
      },
      { type: "heading", text: "Sign 2: There Are Multiple Versions of the Sheet" },
      {
        type: "paragraph",
        text: "The moment two people keep their own copy, you no longer have data - you have opinions. A CRM is one shared record: every rep works in the same pipeline, and the owner sees the real total, not a merge of three files.",
      },
      { type: "heading", text: "Sign 3: You Can't See the Pipeline at a Glance" },
      {
        type: "paragraph",
        text: "Ask 'how much is likely to close this month?' and if the answer takes an afternoon of filtering, the sheet has outgrown its job. A pipeline view shows every open deal by stage and value instantly.",
      },
      {
        type: "table",
        headers: ["Question", "In Excel", "In a CRM"],
        rows: [
          ["Who owns this lead?", "Depends who typed it", "Assigned, with history"],
          ["When is the next follow-up?", "Not tracked", "Due date + reminder"],
          ["What's the pipeline value?", "Manual filtering", "Live dashboard"],
          ["Why did we lose this deal?", "Rarely recorded", "Captured on close"],
        ],
      },
      { type: "heading", text: "Sign 4: Handovers Lose Context" },
      {
        type: "paragraph",
        text: "When a rep leaves or a lead is reassigned, their spreadsheet notes leave with them. A CRM keeps the full conversation history on the lead, so anyone can pick it up without starting from zero.",
      },
      { type: "heading", text: "Sign 5: Sales and Accounts Re-Enter Everything" },
      {
        type: "paragraph",
        text: "If a won deal is re-typed into your accounting software to raise an invoice, that's duplicate work and a chance for errors. In ABSS, a won lead becomes a quote and then a GST invoice in ABSS Books with no re-entry.",
      },
      {
        type: "callout",
        title: "The good news",
        text: "You don't have to abandon what works about spreadsheets. Export your existing lead list to Excel/CSV and import it into ABSS CRM in minutes - your history comes with you.",
      },
    ],
    cta: {
      badge: "Grow Up From Excel",
      title: "Move Your Sales Out of Spreadsheets",
      description: "Import your existing leads and run a real pipeline - with reminders, ownership, and reports.",
      primary: { label: "Book a Demo", href: "/contact" },
      secondary: { label: "Explore ABSS CRM", href: "/features/crm" },
    },
    related: ["qr-code-lead-capture-trade-shows", "lead-to-invoice-crm-books-integration", "debtors-aging-report-collections"],
  },

  // 6 ──────────────────────────────────────────────────────────────────────
  "e-way-bill-for-manufacturers": {
    slug: "e-way-bill-for-manufacturers",
    image: "/images/blog-eway-bill.jpg",
    title:
      "E-Way Bill for Manufacturers: What Triggers It, How to Generate It, and Common Mistakes",
    excerpt:
      "E-Way Bill is required for shipments above ₹50,000. Manufacturers frequently make mistakes on Part B. This guide covers the full workflow in ABSS Books.",
    category: "GST & Compliance",
    tag: "E-Way Bill",
    date: "15 Apr 2026",
    readTime: "7 min read",
    author: { name: "Priya Sharma", role: "GST & Compliance Writer, ABSS", initial: "P" },
    heroBadge: "Required for consignments above ₹50,000",
    intro: [
      "An E-Way Bill is the electronic document that must accompany goods in transit above ₹50,000 in value. For manufacturers moving raw material, semi-finished goods between units, and finished stock to customers, it's a daily operation - and a daily source of small mistakes that cause big delays at check posts.",
      "This guide covers exactly what triggers an E-Way Bill, how ABSS Books generates it alongside the invoice, and the Part B mistakes manufacturers make most often.",
    ],
    tldr: [
      "E-Way Bill is mandatory for consignments above ₹50,000",
      "Part A is invoice detail; Part B is the vehicle number",
      "ABSS Books generates the E-Way Bill from the invoice in one click",
      "Most delays come from a missing or wrong Part B vehicle update",
    ],
    sections: [
      { type: "heading", text: "What Triggers an E-Way Bill?" },
      {
        type: "paragraph",
        text: "An E-Way Bill is required whenever goods worth more than ₹50,000 move - whether it's a sale, a branch transfer, a job-work dispatch, or a return. Manufacturers hit all of these, which is why an automated workflow matters more here than for a pure trader.",
      },
      {
        type: "checkGrid",
        items: [
          { label: "Sale to a customer > ₹50,000", applies: true, note: "Standard outward supply" },
          { label: "Inter-unit / branch transfer", applies: true, note: "Even without a sale value" },
          { label: "Goods sent for job work", applies: true, note: "Against a delivery challan" },
          { label: "Consignment ≤ ₹50,000", applies: false, note: "Generally not required" },
          { label: "Sales return / rejection", applies: true, note: "Movement still needs a bill" },
          { label: "Non-motorised transport", applies: false, note: "Exempt from E-Way Bill" },
        ],
      },
      { type: "heading", text: "Part A vs Part B" },
      {
        type: "table",
        headers: ["Part", "Contains", "Filled By"],
        rows: [
          ["Part A", "GSTINs, invoice no., value, HSN, place of supply", "Auto-filled from the ABSS invoice"],
          ["Part B", "Transporter ID / vehicle number", "Added at dispatch, before the vehicle moves"],
        ],
      },
      { type: "heading", text: "Generate an E-Way Bill in ABSS Books" },
      {
        type: "steps",
        items: [
          { step: "01", title: "Create the invoice or challan", desc: "Part A is built automatically from the document - GSTINs, values, and HSN codes are already there." },
          { step: "02", title: "Click 'Generate E-Way Bill'", desc: "ABSS sends Part A to the NIC portal and returns the 12-digit E-Way Bill number instantly." },
          { step: "03", title: "Enter Part B (vehicle)", desc: "Add the vehicle number or transporter ID. This is the step most teams forget - the bill isn't valid for transit without it." },
          { step: "04", title: "Print and dispatch", desc: "Print the E-Way Bill with the invoice. Update the vehicle number in ABSS if the goods are trans-shipped en route." },
        ],
      },
      { type: "heading", text: "Common Mistakes and How to Avoid Them" },
      {
        type: "errorCards",
        items: [
          { code: "Part B", title: "Vehicle number never updated", fix: "A Part A without Part B is invalid in transit. ABSS reminds you to add the vehicle before the goods leave - never dispatch on Part A alone." },
          { code: "Distance", title: "Wrong distance / expired validity", fix: "Validity is based on distance (roughly one day per 200 km). Enter the correct PIN-to-PIN distance so the bill doesn't expire mid-transit." },
          { code: "Transfer", title: "No bill for branch transfers", fix: "Inter-unit movement needs an E-Way Bill even without a sale. Raise a delivery challan in ABSS and generate the bill against it." },
          { code: "Cancel", title: "Cancelling too late", fix: "An E-Way Bill can only be cancelled within 24 hours and only if goods haven't moved. Cancel or amend promptly from the ABSS document." },
        ],
      },
    ],
    cta: {
      badge: "Move Goods Cleanly",
      title: "Generate E-Way Bills Straight From Your Invoice",
      description: "Part A auto-filled, Part B reminded, validity calculated - no check-post surprises.",
      primary: { label: "Book a Demo", href: "/contact" },
      secondary: { label: "Read the Docs", href: "/documentation" },
    },
    related: ["e-invoice-irn-automatic-b2b", "multi-branch-accounting"],
  },

  // 7 ──────────────────────────────────────────────────────────────────────
  "multi-branch-accounting": {
    slug: "multi-branch-accounting",
    image: "/images/blog-multi-branch.jpg",
    title:
      "Multi-Branch Accounting: Managing 3 Locations Without Hiring 3 Accountants",
    excerpt:
      "Running multiple branches doesn't have to mean multiple accounts, multiple spreadsheets, and multiple CAs. Here's how ABSS Books centralises everything.",
    category: "Accounting & Finance",
    tag: "Multi-Branch",
    date: "10 Apr 2026",
    readTime: "6 min read",
    author: { name: "Rahul Mehta", role: "Accounting Product Lead, ABSS", initial: "R" },
    heroBadge: "One set of books, every location, live",
    intro: [
      "When a business opens its second and third location, accounting usually fractures: each branch keeps its own file, consolidation happens in a spreadsheet at month-end, and by then nobody trusts the numbers. The instinctive fix - hire an accountant per branch - is expensive and still leaves you reconciling three systems.",
      "The better answer is one cloud ledger with branch tagging. Every branch works in the same ABSS Books company, but each transaction knows which location it belongs to. You get branch-level detail and a consolidated view without any manual merging.",
    ],
    tldr: [
      "Keep one company file with a branch tag on every transaction",
      "See per-branch P&L or a consolidated view instantly",
      "Central purchasing and inter-branch stock transfers stay in sync",
      "One accountant can oversee all branches from anywhere",
    ],
    sections: [
      { type: "heading", text: "The Real Cost of Separate Branch Books" },
      {
        type: "paragraph",
        text: "Separate books per branch don't just cost extra salaries. They cost a slow month-end, mismatched inter-branch entries, stock that looks available in one file and sold in another, and an owner who can't answer 'which branch is actually profitable?' until weeks after the month closes.",
      },
      { type: "heading", text: "How Branch Tagging Works" },
      {
        type: "paragraph",
        text: "In ABSS Books you define each location once, then every invoice, bill, payment, and stock movement carries its branch. Reports can be filtered to a single branch or rolled up across all of them - the same data, two lenses.",
      },
      {
        type: "table",
        headers: ["Report", "Per Branch", "Consolidated"],
        rows: [
          ["Profit & Loss", "Yes - by location", "Yes - whole company"],
          ["Stock on hand", "Per warehouse/branch", "Group total"],
          ["Receivables", "By branch's customers", "Company debtors"],
          ["GST liability", "Per GSTIN registration", "All registrations"],
        ],
      },
      { type: "heading", text: "Inter-Branch Transfers Without the Mess" },
      {
        type: "steps",
        items: [
          { step: "01", title: "Raise a transfer challan", desc: "Move stock from Branch A to Branch B with a delivery challan - and an E-Way Bill if it's above ₹50,000." },
          { step: "02", title: "Stock updates both sides", desc: "Branch A's stock reduces and Branch B's increases automatically - no double entry, no mismatch." },
          { step: "03", title: "Reconcile centrally", desc: "The head office sees both legs of every transfer in one place, so nothing goes missing between locations." },
        ],
      },
      { type: "heading", text: "One Accountant, Every Branch" },
      {
        type: "paragraph",
        text: "Because everything is in one cloud company, a single accountant - or your external CA - logs in and works across all branches with role-based access. You scale locations without scaling the finance team at the same rate.",
      },
      {
        type: "callout",
        title: "Multiple GSTINs?",
        text: "If your branches are registered in different states, ABSS handles multiple GSTINs under one company - each branch files against its own registration while you still see the group picture.",
      },
    ],
    cta: {
      badge: "Scale Cleanly",
      title: "Run Every Branch From One Set of Books",
      description: "Branch-level detail and a consolidated view - without hiring an accountant per location.",
      primary: { label: "Book a Demo", href: "/contact" },
      secondary: { label: "Explore ABSS Books", href: "/features/books" },
    },
    related: ["desktop-vs-cloud-accounting-2026", "debtors-aging-report-collections", "e-way-bill-for-manufacturers"],
  },

  // 8 ──────────────────────────────────────────────────────────────────────
  "lead-to-invoice-crm-books-integration": {
    slug: "lead-to-invoice-crm-books-integration",
    image: "/images/blog-crm-books-sync.jpg",
    title:
      "From Lead to Invoice Without Re-Entering Data: How ABSS Bridges Sales and Accounts",
    excerpt:
      "The handoff between sales and accounts is where data gets lost, re-entered, and corrupted. Here's how the CRM-to-Books integration in ABSS eliminates this entirely.",
    category: "Product",
    tag: "Integration",
    date: "5 Apr 2026",
    readTime: "5 min read",
    author: { name: "Sana Kapoor", role: "Product Writer, ABSS", initial: "S" },
    heroBadge: "One record, from first contact to paid invoice",
    intro: [
      "In most businesses, sales and accounts live in different tools. A deal closes in the CRM (or a spreadsheet), then someone re-types the customer, the items, and the prices into the accounting software to raise an invoice. Every re-entry is a chance to get a GSTIN wrong, a rate wrong, or a discount lost.",
      "ABSS puts CRM and Books on the same platform, so a won deal becomes an invoice with no re-typing. Here's how the handoff actually works.",
    ],
    tldr: [
      "CRM and Books share one customer and item database",
      "A won quote converts to a GST invoice with one click",
      "No re-typing means no GSTIN, price, or discount errors",
      "Accounts sees the deal context; sales sees the payment status",
    ],
    sections: [
      { type: "heading", text: "Where the Handoff Usually Breaks" },
      {
        type: "paragraph",
        text: "The gap between 'deal won' and 'invoice raised' is where data quietly corrupts. The salesperson agreed a price and a discount; the accounts team re-enters from a forwarded message and gets one line wrong. The customer disputes the invoice, payment slips, and everyone blames the other team.",
      },
      {
        type: "checkGrid",
        items: [
          { label: "Customer details re-typed", applies: false, note: "Shared master - entered once" },
          { label: "Item prices re-keyed", applies: false, note: "Carried from the quote" },
          { label: "Discounts lost in handoff", applies: false, note: "Travel with the line item" },
          { label: "One click quote → invoice", applies: true, note: "No duplicate data entry" },
        ],
      },
      { type: "heading", text: "The ABSS Flow: One Record, Two Teams" },
      {
        type: "steps",
        items: [
          { step: "01", title: "Lead & quote in CRM", desc: "Sales works the lead in the pipeline and builds a quote using items and prices from the shared catalogue." },
          { step: "02", title: "Deal is won", desc: "When the customer accepts, the quote is marked won - the customer and line items are already in the shared master." },
          { step: "03", title: "Convert to invoice in Books", desc: "One click turns the quote into a GST invoice in ABSS Books - same customer, same items, same prices, plus E-Invoice/E-Way Bill if needed." },
          { step: "04", title: "Payment status flows back", desc: "When the invoice is paid, the CRM shows it - so sales knows which customers are current and which need a nudge." },
        ],
      },
      { type: "heading", text: "Why a Shared Database Beats an Integration" },
      {
        type: "paragraph",
        text: "Bolting a separate CRM onto separate accounting software with a sync connector means webhooks that break, fields that don't map, and two sources of truth drifting apart. Because ABSS Books and ABSS CRM are one platform, there's nothing to sync - it's the same customer and item record on both sides.",
      },
      {
        type: "callout",
        title: "The payoff",
        text: "Faster invoicing, fewer disputes, and a single timeline per customer - from first enquiry to final payment - that both sales and accounts can trust.",
      },
    ],
    cta: {
      badge: "Connect Sales & Accounts",
      title: "Close a Deal and Invoice It in One Click",
      description: "See how a won CRM quote becomes a GST invoice in ABSS Books with zero re-entry.",
      primary: { label: "Book a Demo", href: "/contact" },
      secondary: { label: "Explore ABSS CRM", href: "/features/crm" },
    },
    related: ["signs-outgrown-excel-need-crm", "qr-code-lead-capture-trade-shows", "debtors-aging-report-collections"],
  },

  // 9 ──────────────────────────────────────────────────────────────────────
  "debtors-aging-report-collections": {
    slug: "debtors-aging-report-collections",
    image: "/images/blog-debtors-aging.jpg",
    title:
      "Debtors Aging Report Explained: How to Use It to Speed Up Collections",
    excerpt:
      "Your debtors aging report tells you exactly who owes you money and for how long. Here's how to read it, act on it, and reduce overdue receivables using ABSS Books + CRM.",
    category: "Accounting & Finance",
    tag: "Reports",
    date: "28 Mar 2026",
    readTime: "5 min read",
    author: { name: "Rahul Mehta", role: "Accounting Product Lead, ABSS", initial: "R" },
    heroBadge: "Turn overdue receivables into collected cash",
    intro: [
      "A debtors aging report is one of the most useful pages in your accounts and one of the least used. It groups every unpaid customer invoice by how overdue it is, so you can see at a glance where your cash is stuck - and whom to chase first.",
      "This guide explains how to read the report, how to prioritise collections with it, and how ABSS Books and CRM together turn it from a static list into an action plan.",
    ],
    tldr: [
      "Aging buckets group unpaid invoices by how overdue they are",
      "The 90+ day bucket is where cash - and risk - concentrates",
      "Prioritise by amount and age, not by who called last",
      "ABSS links each overdue invoice to a follow-up in the CRM",
    ],
    sections: [
      { type: "heading", text: "How to Read the Report" },
      {
        type: "paragraph",
        text: "The report lists each customer and splits what they owe into aging buckets counted from the invoice due date. The further right the money sits, the harder it is to collect - and the more it signals a customer who needs attention.",
      },
      {
        type: "table",
        headers: ["Bucket", "Meaning", "Action"],
        rows: [
          ["0–30 days", "Within or just past terms", "Gentle reminder"],
          ["31–60 days", "Slipping", "Call and confirm a pay date"],
          ["61–90 days", "Overdue", "Escalate to the account owner"],
          ["90+ days", "At risk", "Firm follow-up; consider holding supply"],
        ],
      },
      { type: "heading", text: "Prioritise by Amount × Age" },
      {
        type: "paragraph",
        text: "Don't chase alphabetically or by whoever complained last. The invoices worth your time are large and old. Sort by the 90+ bucket first, then by value, and you'll recover the most cash for the least effort.",
      },
      {
        type: "checkGrid",
        items: [
          { label: "Large + 90+ days", applies: true, note: "Chase first - highest cash at risk" },
          { label: "Large + recently due", applies: true, note: "A quick reminder protects the balance" },
          { label: "Small + 90+ days", applies: true, note: "Batch these into one collection run" },
          { label: "Small + within terms", applies: false, note: "Not worth active chasing yet" },
        ],
      },
      { type: "heading", text: "From Report to Recovered Cash in ABSS" },
      {
        type: "steps",
        items: [
          { step: "01", title: "Open Debtors Aging in ABSS Books", desc: "See every customer's outstanding split across the aging buckets, with drill-down to the exact invoices." },
          { step: "02", title: "Turn overdue invoices into tasks", desc: "Assign a follow-up in ABSS CRM against the customer, with a due date and the invoice attached - so the chase doesn't depend on memory." },
          { step: "03", title: "Record the promise-to-pay", desc: "Log the committed pay date on the lead. If it slips, the reminder fires again automatically." },
          { step: "04", title: "Watch the buckets shrink", desc: "As payments post, invoices drop out of the older buckets - your report becomes a live scoreboard for collections." },
        ],
      },
      {
        type: "callout",
        title: "Prevention beats collection",
        text: "Set clear credit terms per customer in ABSS and let the system flag a customer before you ship more goods to an already-overdue account. The cheapest overdue invoice is the one you never create.",
      },
    ],
    cta: {
      badge: "Collect Faster",
      title: "Turn Your Aging Report Into Collected Cash",
      description: "Link every overdue invoice to a follow-up and watch your 90+ day bucket shrink.",
      primary: { label: "Book a Demo", href: "/contact" },
      secondary: { label: "Explore ABSS Books", href: "/features/books" },
    },
    related: ["multi-branch-accounting", "lead-to-invoice-crm-books-integration"],
  },
};

export const getPost = (slug) => BLOG_POSTS[slug] || null;

export const getRelated = (slugs = []) =>
  slugs
    .map((s) => BLOG_POSTS[s])
    .filter(Boolean)
    .map((p) => ({
      slug: p.slug,
      title: p.title,
      category: p.category,
      date: p.date,
      readTime: p.readTime,
      image: p.image,
    }));

export const ALL_SLUGS = Object.keys(BLOG_POSTS);
